/**
 * Wire contract for the submit-quote command link
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md §5, §6).
 */

export const ExistingQuoteStateValue = {
  ACTIVE: "ACTIVE",
  COUNTERED: "COUNTERED",
} as const;
export type ExistingQuoteStateValue =
  (typeof ExistingQuoteStateValue)[keyof typeof ExistingQuoteStateValue];

export interface ExistingQuoteState {
  // null when the carrier holds no live quote on the shipment.
  state: ExistingQuoteStateValue | null;
  price: string | null;
  currency: string | null;
}

export interface QuoteEligibilityPreview {
  can_quote: boolean;
  reason_code: string | null;
}

/**
 * The shipment as the preview publishes it. Deliberately not
 * `CarrierCondensedShipment`: that is the authenticated carrier API's shape, and
 * this endpoint takes a signed token as its whole authorization, so its payload
 * is an allowlist maintained alongside
 * `application/commandLink/command_links/submit_quote/serializers.py`. Typing it
 * as the wider shape would let this page read fields the wire no longer carries.
 */
export interface SubmitQuotePreviewShipmentInfo {
  id: number;
  type: ShipmentType;
  service: string;
  commodity: Commodity | null;
  commodity_freetext: string | null;
  weight_lb: number;
  footage: number | null;
  additional_details: string | null;
  special_requirements: string;
  quoting_unit_of_measure: UnitOfMeasureType;
  created_at: string;
}

/**
 * The suggested route, narrowed to what the map draws. The backend allowlist
 * drops the routing request metadata and the row's foreign keys, so the wider
 * global `SuggestedRouteNavigation` would promise fields this wire never sends.
 */
export interface SubmitQuotePreviewNavigation {
  payload: SuggestedRouteNavigationPayload;
  distance_meters: number;
  duration_seconds: number;
}

export interface SubmitQuotePreviewShipment {
  info: SubmitQuotePreviewShipmentInfo;
  equipments: Equipment[];
  shipment_company: CondensedShipmentCompany;
  shipment_status: ShipmentStatusValue;
  // Kept alongside route_points because the quote form seeds its pickup and
  // delivery dates from the first and last stop.
  first_point: RoutePoint;
  last_point: RoutePoint;
  route_points: RoutePoint[];
  navigation: SubmitQuotePreviewNavigation | null;
  project: { loads_available: number } | null;
  is_book_now: boolean;
  // A count, not the quotes: the names stay behind login, so the page shows an
  // advisory and sends the carrier to the app to see who.
  teammate_quote_count: number;
}

export interface SubmitQuotePreviewData {
  shipment: SubmitQuotePreviewShipment;
  answered_question_count: number;
  existing_quote: ExistingQuoteState;
  eligibility: QuoteEligibilityPreview;
  has_marked_no_capacity: boolean;
}

export interface SubmitQuoteExecuteResponse {
  quote_id: number | null;
  success: boolean;
  // A recoverable failure code, e.g. ACTIVE_QUOTE_EXISTS or PRICE_BELOW_ZERO.
  outcome_code: string | null;
  /**
   * A stop the carrier cannot correct from the form, when it is one the preview
   * also publishes — the shipment was cancelled, booked or expired since the
   * page loaded, or their own company or plan state changed. Keyed the same way
   * as the preview's `eligibility.reason_code`, so both render the same copy.
   * Permission-boundary rejections are not reported here; those stay a 403 with
   * an empty body.
   */
  blocked_reason_code: string | null;
}

export interface SubmitQuoteParams {
  equipment_type: string;
  pickup_start: string;
  pickup_start_time: null;
  delivery_eta: string;
  delivery_eta_time: null;
  price: number | null;
  currency: string;
  notes: string;
  tracking_link_offered: boolean;
  quoting_unit_of_measure: string;
  min_loads_desired?: number | null;
  max_loads_desired?: number | null;
}
